import React, { useState, useEffect } from 'react';
import { Lightbulb, LightbulbOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPublicPackages } from '../services/publicApi';
import '../styles/packages.css';

const HIDDEN_TIER_KEYS = ['lpu', 'lpu_faculty', 'institutional_lpu', 'lpu_premium'];

export default function Packages({ billingCycle = 'monthly' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const res = await getPublicPackages();
        if (res.status === 'success') {
          const visible = res.data
            .filter(p => !HIDDEN_TIER_KEYS.includes((p.tier_key || '').toLowerCase()))
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          setPackages(visible);
        }
      } catch (err) {
        console.error('Failed to load packages from API, using fallback', err);
        // Fallback or empty state handled by loading check
      } finally {
        setLoading(false);
      }
    };

    fetchTiers();
  }, []);

  const handleUpgradeClick = (e, packageName) => {
    e.preventDefault();

    // For Institutional plan, always go to pricing page to see contact info
    if (packageName === 'Institutional') {
      if (user) {
        navigate('/pricing');
      } else {
        navigate('/login');
      }
      return;
    }

    // If user is logged in, go to pricing page
    // If not logged in, go to login/signup page
    if (user) {
      navigate('/pricing');
    } else {
      if (packageName === 'Freemium') {
        navigate('/signup');
      } else {
        navigate('/login');
      }
    }
  };

  const getPrimaryPrice = (pkg) => {
    const rawPrice = billingCycle === 'yearly' && pkg.yearly_price ? pkg.yearly_price : pkg.price;
    const rawText = typeof rawPrice === 'string' ? rawPrice : String(rawPrice || '');

    // Some backend values include "(Monthly)" inside the price string.
    // Keep that as a small period label instead of large main price text.
    const hasMonthlyInPrice = /\(\s*monthly\s*\)/i.test(rawText);
    const amount = rawText
      .replace(/\(\s*monthly\s*\)/gi, '')
      .replace(/\/-/g, '')
      .trim();

    const period =
      billingCycle === 'yearly' && pkg.yearly_price
        ? ''
        : (pkg.period || (hasMonthlyInPrice ? '/month' : ''));

    return { amount, period };
  };

  if (loading) {
    return (
      <section id="packages" className="packages min-h-[400px] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-8 w-64 bg-slate-100 rounded-full mb-4"></div>
          <div className="h-4 w-48 bg-slate-100 rounded-full"></div>
        </div>
      </section>
    );
  }

  const standardPackages = packages.filter(pkg => pkg.tier_key !== 'institutional');
  const institutionalPackage = packages.find(pkg => pkg.tier_key === 'institutional');
  const institutionalPrice = institutionalPackage ? getPrimaryPrice(institutionalPackage) : null;
  const institutionalDisplayFeatures = institutionalPackage?.features?.length > 0 
    ? institutionalPackage.features.filter(f => f.included).map(f => f.name)
    : [
        'Unlimited Generations',
        'No Weekly Cap',
        'Unlimited visuals/scenarios/exercises',
        'RBT + IKS + LO-PO mapping',
        'Admin control panel',
        'Dedicated support'
      ];

  return (
    <section id="packages" className="packages">
      <div className="container">
        <div className="section-header reveal-down">
          <h2 className="reveal-text">Choose Your Teaching Readiness Path</h2>
          <p>Flexible packages designed for educators of all sizes</p>
        </div>

        <div className="packages-grid">
          {standardPackages.map((pkg, index) => {
            const primaryPrice = getPrimaryPrice(pkg);
            return (
            <div key={index} className={`package-card-wrapper reveal`} style={{ transitionDelay: `${index * 0.15}s` }}>
              <div className={`package-card glass-panel ${pkg.color} ${pkg.featured ? 'featured' : ''}`}>
                {pkg.badge && (
                  <div className="package-badge">
                    {pkg.badge.trim().toUpperCase().includes('BEGIN') ? 'BEGIN' : pkg.badge}
                  </div>
                )}

                <div className="package-header">
                  <div className="package-icon">
                    <img src={pkg.icon} alt={`${pkg.name} icon`} />
                  </div>
                  <h3>{pkg.name}</h3>
                  {pkg.old_price && (
                    <div className="package-old-price">
                      <s>{pkg.old_price}</s> <span>{pkg.period}</span>
                    </div>
                  )}
                  <div className="package-price">
                    {primaryPrice.amount}
                    <span>{primaryPrice.period}</span>
                  </div>
                  {pkg.offer_info && (
                    <div className="package-offer-tag">
                      {pkg.offer_info}
                    </div>
                  )}
                  {pkg.yearly_price && billingCycle === 'monthly' && (
                    <div className="package-yearly-price">
                      {pkg.yearly_price}
                    </div>
                  )}
                  {pkg.subtitle && (
                    <div className="package-subtitle">
                      {pkg.subtitle}
                    </div>
                  )}
                </div>

                <ul className="package-features">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className={feature.included ? 'included' : 'excluded'}>
                      {feature.included ? <Lightbulb size={20} className="icon-included" /> : <LightbulbOff size={20} className="icon-excluded" />}
                      <span>{feature.name}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={(e) => handleUpgradeClick(e, pkg.name)}
                  className={`btn btn-large ${pkg.featured ? 'btn-accent' : pkg.name === 'Freemium' ? 'btn-outline' : 'btn-outline'}`}
                >
                  {pkg.name === 'Freemium' ? 'Start Your Free Journey' : `Upgrade to ${pkg.name}`}
                </button>
              </div>
            </div>
            );
          })}
        </div>

        {institutionalPackage && (
          <div className="institutional-container mt-8 reveal-down">
            <div className="package-card glass-panel institutional horizontal-card">
              <div className="package-header">
                <div className="package-icon">
                  <img src={institutionalPackage.icon} alt={`${institutionalPackage.name} icon`} />
                </div>
                <div className="header-text">
                  <h3>{institutionalPackage.name}</h3>
                  <div className="package-price">
                    {institutionalPrice.amount}
                    <span>{institutionalPrice.period}</span>
                  </div>
                  {institutionalPackage.badge && <span className="package-badge-inline">{institutionalPackage.badge}</span>}
                </div>
              </div>

              <div className="institutional-content">
                <ul className="package-features horizontal-features">
                  {institutionalDisplayFeatures.map((featureName, idx) => (
                    <li key={idx} className="included">
                      <Lightbulb size={18} className="icon-included" />
                      <span>{featureName}</span>
                    </li>
                  ))}
                </ul>

                <div className="institutional-action">
                  <button
                    onClick={(e) => handleUpgradeClick(e, institutionalPackage.name)}
                    className="btn btn-large btn-accent"
                  >
                    Contact Sales
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
